// ScenarioComparisonReport.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
    Container, Typography, Paper, CircularProgress, Alert, Box, Grid,
    Tabs, Tab
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Remove } from '@mui/icons-material';
import { authFetch } from '../authFetch';
import MapViewComparison from './MapViewComparison';

// --- Helper Components ---

const PopulationImpactSummary = ({ summary, baseName, compName }) => {
    const { base_total, comp_total, difference_percent } = summary;
    const getTrendIcon = () => {
        if (difference_percent > 0) return <ArrowUpward fontSize="small" sx={{ color: 'error.main' }} />;
        if (difference_percent < 0) return <ArrowDownward fontSize="small" sx={{ color: 'success.main' }} />;
        return <Remove fontSize="small" />;
    };
    return (
        <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={4}><Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">{baseName}</Typography><Typography variant="h4">{base_total.toLocaleString()}</Typography></Paper></Grid>
            <Grid item xs={12} sm={4}><Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">{compName}</Typography><Typography variant="h4">{comp_total.toLocaleString()}</Typography></Paper></Grid>
            <Grid item xs={12} sm={4}><Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Change</Typography><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getTrendIcon()}<Typography variant="h4" sx={{ color: difference_percent > 0 ? 'error.main' : (difference_percent < 0 ? 'success.main' : 'text.primary') }}>{difference_percent > 0 ? '+' : ''}{difference_percent.toFixed(1)}%</Typography></Box></Paper></Grid>
        </Grid>
    );
};

// --- NEW: Simple Line Chart Component ---
const DemandFrequencyLineChart = ({ title, data, color }) => {
    if (!data || data.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="text.secondary">No unmet demand for this system.</Typography></Box>;
    }
    
    return (
        <Box sx={{ width: '100%', height: 450 }}>
            <Typography component="h3" variant="body2" align="center" gutterBottom sx={{ fontWeight: 'medium' }}>{title}</Typography>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                        type="number" dataKey="unmet_demand" name="Per capita demand units" scale="log" 
                        domain={['dataMin', 'dataMax']}
                        allowDataOverflow 
                        label={{ value: 'Per capita demand units', position: 'insideBottom', offset: -15 }}
                        tickFormatter={(tick) => tick.toLocaleString()}
                    />
                    <YAxis 
                        dataKey="frequency" 
                        name="Cumulative frequency"
                        label={{ 
                            value: 'Cumulative frequency',  // (2) Updated the label text
                            angle: -90, 
                            position: 'insideLeft',         // (3) 'insideLeft' is the correct position
                            offset: 5,                    // (4) Use offset to move it further into the margin
                            style: { textAnchor: 'middle' } // (5) Ensure the text is centered on its position
                        }}
                    />
                    <Tooltip 
                        formatter={(value) => value.toLocaleString()}
                        labelFormatter={(label) => `Demand: ${label.toLocaleString()}`}
                    />
                    <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '30px' }} />
                    <Line 
                        type="monotone" 
                        dataKey="frequency" 
                        name="Frequency Rank" 
                        stroke={color} 
                        strokeWidth={2} 
                        dot={false} 
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

// --- Child Component for Tab Content ---
function SystemComparisonTabPanel({ baseScenario, comparisonScenario, system }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);

    useEffect(() => {
        const fetchSystemData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await authFetch('/api/scenarios/compare_system/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        base_scenario_id: baseScenario.id,
                        comparison_scenario_id: comparisonScenario.id,
                        system: system,
                    }),
                });
                if (!response.ok) throw new Error(`Failed to load comparison data for ${system}: ${response.statusText}`);
                const data = await response.json();
                setComparisonData(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchSystemData();
    }, [baseScenario.id, comparisonScenario.id, system]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    if (!comparisonData) {
        return null;
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Typography component="h3" variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'medium' }}>
                        Local residents affected
                    </Typography>
                    <PopulationImpactSummary 
                        summary={comparisonData.system_level_summary}
                        baseName={baseScenario.name}
                        compName={comparisonScenario.name}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Typography component="h3" variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'medium' }}>
                        Unmet demand distribution
                    </Typography>
                    <Grid container spacing={4} alignItems="stretch">
                        <Grid item xs={12} md={6}>
                            <DemandFrequencyLineChart title={`Base: ${baseScenario.name}`} data={comparisonData.plot_data.base[system]} color="#8884d8" />
                        </Grid>
                        <Grid item xs={12} md={6}>
                           <DemandFrequencyLineChart title={`Comparison: ${comparisonScenario.name}`} data={comparisonData.plot_data.comparison[system]} color="#82ca9d" />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
}


// --- Main Parent Component (Unchanged) ---
export default function ScenarioComparisonReport() {
    const { baseScenarioId, comparisonScenarioId } = useParams();
    const [scenarioInfo, setScenarioInfo] = useState({ base: null, comparison: null });
    const [commonSystems, setCommonSystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [baseInfoRes, compInfoRes] = await Promise.all([
                    authFetch(`/api/scenarios/${baseScenarioId}/`),
                    authFetch(`/api/scenarios/${comparisonScenarioId}/`),
                ]);
                const baseInfo = await baseInfoRes.json();
                const compInfo = await compInfoRes.json();
                setScenarioInfo({ base: baseInfo, comparison: compInfo });

                const baseSystems = baseInfo.selected_systems || [];
                const compSystems = compInfo.selected_systems || [];
                const common = baseSystems.filter(sys => compSystems.includes(sys));
                setCommonSystems(common.sort());

                if (common.length === 0) {
                    setError("No common systems with analysis results found to compare between these two scenarios.");
                }

            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [baseScenarioId, comparisonScenarioId]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (<Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading Scenario Information...</Typography></Container>);
    }

    if (error) {
        return <Container sx={{ pt: 4 }}><Alert severity="error">{error}</Alert></Container>;
    }

    const selectedSystem = commonSystems[activeTab];

    return (
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' }, margin: 'auto', p: { xs: 1, sm: 2 } }}>
            <Typography component="h2" variant="h6" gutterBottom>
                Scenario Comparison
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
                Comparing "{scenarioInfo.base?.name}" vs "{scenarioInfo.comparison?.name}"
            </Typography>

            <Paper sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="system comparison tabs"
                >
                    {commonSystems.map(sys => (
                        <Tab key={sys} label={sys.charAt(0).toUpperCase() + sys.slice(1)} />
                    ))}
                </Tabs>
            </Paper>

            {selectedSystem && (
                <Box sx={{ mt: 3 }}>
                    <SystemComparisonTabPanel
                        baseScenario={scenarioInfo.base}
                        comparisonScenario={scenarioInfo.comparison}
                        system={selectedSystem}
                    />
                </Box>
            )}

            <Paper sx={{ p: 2, mt: 3 }}>
                <Typography component="h3" variant="subtitle1" gutterBottom align="center" sx={{ fontWeight: 'medium' }}>
                    Service flow change
                </Typography>
                <Box sx={{ width: '100%', height: '70vh', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200', mt: 2 }}>
                    {selectedSystem && (
                        <MapViewComparison
                            key={selectedSystem}
                            baseScenarioId={baseScenarioId}
                            comparisonScenarioId={comparisonScenarioId}
                            system={selectedSystem}
                        />
                    )}
                </Box>
            </Paper>
        </Box>
    );
}